import { X as XIcon, PencilIcon, SendIcon } from 'lucide-react';
import { useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { StatusSelect } from '@/components/ui/status-select';
import type { KanbanColumn } from '@/types/kanban';
import type { CandidateStatusId } from '@/types/candidate';

type BulkStatusUpdateProps = {
  columns: KanbanColumn[];
  statusToUpdate: CandidateStatusId | null;
  onStatusChange: (statusId: CandidateStatusId) => void;
  onApply: () => void;
  selectedCount: number;
  onClearSelection: () => void;
  onEmailSend?: () => void;
  emailDisabled?: boolean;
};

export const BulkStatusUpdate = ({
  columns,
  statusToUpdate,
  onStatusChange,
  onApply,
  selectedCount,
  onClearSelection,
  onEmailSend,
  emailDisabled = false,
}: BulkStatusUpdateProps) => {
  if (selectedCount === 0) {
    return null;
  }

  const selectionLabel =
    selectedCount === 1 ? 'kandidaat geselecteerd' : 'kandidaten geselecteerd';
  const statusOptions = useMemo(
    () =>
      columns.map((column) => ({
        id: column.id,
        label: column.name,
        color: column.color,
      })),
    [columns]
  );
  const isStatusSelected = Boolean(statusToUpdate);
  const disableStatusAction = selectedCount === 0 || !isStatusSelected;

  return (
    <div className="fixed inset-x-6 bottom-6 z-20">
      <div className="flex flex-col gap-4 rounded-lg border bg-white border-zinc-200 p-4 shadow-lg sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <Button
            className="inline-flex h-8 w-8 items-center justify-center rounded-md border text-sm font-semibold hover:bg-muted focus:outline-none focus:ring-2 focus:ring-primary"
            type="button"
            onClick={onClearSelection}
            aria-label="Deselecteer alle kandidaten"
          >
            <XIcon className='h-4 w-4 text-white' strokeWidth={3}></XIcon>
          </Button>
          <span className="text-sm font-medium">
            {selectedCount} {selectionLabel}
          </span>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
          <label className="flex flex-col gap-1 text-sm font-medium sm:flex-row sm:items-center sm:gap-2">
            <span>Status wijziging:</span>
            <StatusSelect
              className="w-fit h-9 px-3 py-1.5"
              value={statusToUpdate ?? undefined}
              options={statusOptions}
              onChange={onStatusChange}
              placeholder="Kies status"
            />
          </label>
          <button
            className={`inline-flex text-white items-center justify-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
              disableStatusAction ? 'cursor-not-allowed opacity-60' : 'hover:bg-primary/90'
            }`}
            type="button"
            onClick={onApply}
            disabled={disableStatusAction}
          >
            <PencilIcon className="h-4 w-4" />
            Status wijzigen
          </button>
          {onEmailSend ? (
            <button
              className="inline-flex items-center justify-center gap-2 rounded-md border border-primary px-4 py-2 text-sm font-medium text-primary shadow hover:bg-primary/10 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
              type="button"
              onClick={onEmailSend}
              disabled={selectedCount === 0 || emailDisabled}
            >
              <SendIcon className="h-4 w-4" />
              Email Versturen
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
};
